/**
 * StreamNotes — what PE said about a stream, in the coordinator's words (Oct 25).
 *
 * Two things that used to reach only the server log:
 *   - PE's soft warnings on the last author and the last finalize (a scene over
 *     its time, photos it couldn't time to the narration, a manual timing lost,
 *     a layout repaired);
 *   - a failed finalize, which the server records as structured JSON and the
 *     editor never showed: the stream simply stayed in review.
 *
 * Wording here is a first draft for David.
 */
import React from "react";

// Codes that are CG's own bookkeeping, not something a coordinator can act on.
const HIDDEN = new Set(["document_uncitable"]);

const SAYS = {
  narration_over_budget: "The narration is longer than this scene's word budget.",
  scene_over_duration:   "This scene runs longer than its time limit.",
  cue_alignment_failed:  "The photos couldn't be timed to the narration, so they're evenly spaced.",
  cue_lock_lost:         "A photo's timing couldn't be kept after the narration changed.",
  layout_order_repaired: "The layout left some photos out; they were put back.",
  layout_fell_back:      "The layout couldn't be designed, so the photos play as a simple slideshow.",
  selection_repeats:     "There weren't enough photos for every scene, so some appear more than once.",
  card_text_reset:       "A text card's words were no longer in the narration, so it shows the start of that line instead.",
  support_gap:           "Nothing in the sources says what's hard here or how staff can help, so there's no scene about it. An interview could fill the gap.",
  card_text_fell_back:   "Words for some text cards couldn't be chosen, so they show the start of each line.",
};

export function warningText(w) {
  if (!w) return "";
  return SAYS[w.code] || w.message || w.code || "";
}

/** A failed finalize/author, readable. Plain-text errors pass through. */
export function friendlyStreamError(error, titleOf = id => id) {
  if (!error) return null;
  let obj = null;
  try { obj = JSON.parse(error); } catch { return error; }
  if (!obj || typeof obj !== "object") return error;
  if (obj.error_code !== "composition_failed") return obj.detail || obj.message || error;
  const where = obj.scene_id && obj.scene_id !== "unknown" ? ` for “${titleOf(obj.scene_id)}”` : "";
  if (obj.failed_attribute === "narration_audio") {
    return `The narration${where} didn't generate. Nothing was lost — press Finalize to try again.`;
  }
  const again = obj.failure_kind === "transient" ? " This is usually temporary — try again." : "";
  return `The stream couldn't be put together${where}.${again}`;
}

export default function StreamNotes({ authorWarnings, finalizeWarnings, error, titleOf = id => id }) {
  const shown = list => (list || []).filter(w => w && !HIDDEN.has(w.code) && warningText(w));
  const author = shown(authorWarnings);
  const final  = shown(finalizeWarnings);
  const failed = friendlyStreamError(error, titleOf);
  if (!failed && !author.length && !final.length) return null;

  const row = (w, i) => (
    <li key={i} style={n.item}>
      {w.scene_id && <strong>{titleOf(w.scene_id)}: </strong>}
      {warningText(w)}
    </li>
  );
  return (
    <div style={n.box} aria-label="Notes on this stream">
      {failed && <p style={n.error} role="alert">{failed}</p>}
      {author.length > 0 && (
        <>
          <p style={n.head}>Notes from authoring</p>
          <ul style={n.list}>{author.map(row)}</ul>
        </>
      )}
      {final.length > 0 && (
        <>
          <p style={n.head}>Notes from the last finalize</p>
          <ul style={n.list}>{final.map(row)}</ul>
        </>
      )}
    </div>
  );
}

const n = {
  box:   { background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  head:  { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)", margin: 0, textTransform: "uppercase", letterSpacing: "0.05em" },
  list:  { margin: 0, paddingLeft: "var(--space-5)", display: "flex", flexDirection: "column", gap: 2 },
  item:  { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  error: { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
};
